import { InputField, colors, View } from '@fairygodboss/ui';
import {
    useRef, useState, useEffect, useCallback
} from 'react';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Window {
    google: any;
    _googleMapsStartedLoading: boolean;
}

export const LocationInput = ({
    value,
    googlePlaceAPITypes,
    googlePlaceAPIComponentRestrictions,
    changeHandler,
    coordinates: incomingCoordenates,
    ...others
}) => {
    const inputRef = useRef();
    const containerRef = useRef();
    const [isGoogleSet, setIsGoogleSet] = useState(false);
    const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
    const [googleField, setGoogleField] = useState(null);
    const [coordinates, setCoordinates] = useState(
        incomingCoordenates || {
            lat: null,
            lng: null,
        }
    );

    const handleChange = useCallback(
        (location) => {
            changeHandler(location);
        },
        [changeHandler]
    );

    const handleInputChange = (e) => {
        if (googleField) {
            relocatePacContainer();
        }
        handleChange({
            coordinates,
            value: e.target.value,
        });
    };

    const relocatePacContainer = () => {
        // eslint-disable-next-line no-underscore-dangle
        const autocompleteContainer = document.querySelectorAll('.pac-container');
        const targetContainer = document.querySelector(
            '.location-input-container'
        );
        if (autocompleteContainer && targetContainer) {
            autocompleteContainer.forEach((container) => {
                if (container.parentNode !== targetContainer) targetContainer.appendChild(container);
            });
            return true;
        }
        return false;
    };

    const setUpGoogle = useCallback(() => {
        if (
            window.google
            && window.google.maps
            && !isGoogleSet
            && inputRef?.current
        ) {
            const element = inputRef.current;
            const autocomplete = new window.google.maps.places.Autocomplete(
                element,
                {
                    types: googlePlaceAPITypes,
                    componentRestrictions: googlePlaceAPIComponentRestrictions,
                }
            );

            autocomplete.setFields([
                'address_components',
                'formatted_address',
                'geometry',
            ]);

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                const address = place.formatted_address;
                setCoordinates({
                    lat,
                    lng,
                });
                handleChange({
                    lat,
                    lng,
                    address,
                    value: address,
                });
            });
            setGoogleField(autocomplete);
            setIsGoogleSet(true);
        }
    }, [
        googlePlaceAPITypes,
        googlePlaceAPIComponentRestrictions,
        handleChange,
        isGoogleSet,
        coordinates,
        inputRef,
    ]);

    const loadGoogleScript = useCallback(() => {
        const script = document.createElement('script');
        script.onload = () => {
            setUpGoogle();
        };
        script.src = 'https://maps.googleapis.com/maps/api/js?v=3.47.6&key=AIzaSyBFts9V0cwwsvKe6JdP8VcD9FLuYp5iWxU&libraries=places';
        document.head.appendChild(script);
    }, [setUpGoogle]);

    useEffect(() => {
        // eslint-disable-next-line no-underscore-dangle
        if (!window.google && !googleMapsLoaded) {
            // eslint-disable-next-line no-underscore-dangle
            loadGoogleScript();
            setGoogleMapsLoaded(true);
        } else {
            setTimeout(() => {
                setUpGoogle();
            }, 2000);
        }
    }, [googleMapsLoaded, setUpGoogle]);

    const handleRightIconClick = () => {
        handleChange({
            lat: null,
            lng: null,
            value: '',
        });
    };

    const rightIconProps = () => {
        if (value !== '' && value !== null) {
            return {
                useRightIcon: true,
                rightIconCursor: 'pointer',
                rightIconName: 'close_16',
                rightIconProps: {
                    outlineColor: colors.neutral[50],
                },
                rightIconClick: handleRightIconClick,
            };
        }
        return {};
    };

    return (
        <View
            data-qa="Jobs-Searchbar-Location"
            ref={containerRef}
            css={{ position: 'relative' }}
            className="location-input-container"
        >
            <style jsx global>
                {`
                    .pac-container {
                        left: 0 !important;
                        top: 48px !important;
                        z-index: 8888888;
                        overflow: auto;
                        height: 150px;
                        background-color: white;
                    }
                    .pac-container {
                        border-radius: 5px;
                    }
                    .pac-icon.pac-icon-marker {
                        display: none;
                    }
                    .pac-item {
                        padding: 16px 10px;
                        border-top: none;
                        font-family: "Open sans", "Lato", sans-serif;
                        font-size: 16px;
                        font-style: normal;
                        line-height: 24px;
                        letter-spacing: 0;
                        color: unset;
                    }
                    .pac-item span {
                        font-size: 16px;
                    }
                    .pac-item-query {
                        font-size: 16px;
                    }
                    .pac-item:hover {
                        background-color: #eff1f5;
                    }
                    .pac-logo::after {
                        display: none;
                    }
                `}
            </style>
            <InputField
                ref={inputRef}
                value={value}
                css={{
                    input: {
                        height: '50px',
                    },
                }}
                changeHandler={handleInputChange}
                useLeftIcon
                leftIconName="location_16"
                placeholder="City, State or Country"
                {...rightIconProps()}
                data-qa="Jobs-Searchbar-Location-input"
                dataQa="Location"
                {...others}
            />
        </View>
    );
};

LocationInput.displayName = 'LocationInput';

export default LocationInput;
